/**
 * `/hr/onboarding` — the Onboarding screen, WP-W6.
 * `web/shapes/hr-onboarding.md` is the agreed shape, written before this file.
 *
 * Two surfaces on one screen, and they authorise differently, which is the whole reason
 * they are two panels rather than one:
 *
 *  - **Templates** are company configuration (`hr.view_employee_hr` at scope company),
 *    with no employee-tree gate. List, create, and — on a selected template — its items.
 *  - **Checklists** are about one employee (a hybrid gate the server resolves over the
 *    tree). Keyed by a typed employee id — `GET /employees` (Task T2-A) and
 *    `EmployeePicker` (Task T2-B) exist now, but this panel has not been rewired to use
 *    them, per `OnboardingChecklistsPanel.tsx`'s own header — they list an
 *    employee's checklists with per-task progress, create one from a template, and
 *    complete tasks — closing the checklist server-side when the last task is done.
 *
 * Nothing is applied optimistically. A read refused renders `ApiErrorNote` in its region
 * and leaves the others standing; a write refused renders the server's own sentence.
 * Completing a task re-reads the checklists, because in that one case the state the
 * controls were drawn from has changed.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import type {
  OnboardingChecklist,
  OnboardingChecklistCreateRequest,
  OnboardingChecklistListResponse,
  OnboardingTemplate,
  OnboardingTemplateCreateRequest,
  OnboardingTemplateItem,
  OnboardingTemplateItemCreateRequest,
  OnboardingTemplateItemListResponse,
  OnboardingTemplateListResponse,
} from '../../api/hr';
import {
  HR_ONBOARDING_CHECKLISTS_PATH,
  HR_ONBOARDING_TEMPLATES_PATH,
  onboardingChecklistsPath,
  onboardingTaskCompletePath,
  onboardingTemplateItemsPath,
} from '../../api/hr';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { OnboardingTemplatesPanel } from './OnboardingTemplatesPanel';
import { OnboardingChecklistsPanel } from './OnboardingChecklistsPanel';

const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';

export function OnboardingScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  const [templatesToken, setTemplatesToken] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [itemsToken, setItemsToken] = useState(0);

  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [checklistsToken, setChecklistsToken] = useState(0);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);

  const templates = useApiQuery<OnboardingTemplateListResponse>(
    HR_ONBOARDING_TEMPLATES_PATH,
    templatesToken,
  );

  const itemsPath =
    selectedTemplateId === null ? null : onboardingTemplateItemsPath(selectedTemplateId);
  const items = useApiQuery<OnboardingTemplateItemListResponse>(itemsPath, itemsToken);

  const checklistsPath =
    employeeId === null ? null : onboardingChecklistsPath({ employee_id: employeeId });
  const checklists = useApiQuery<OnboardingChecklistListResponse>(checklistsPath, checklistsToken);

  const createTemplate = useApiMutation<OnboardingTemplateCreateRequest, OnboardingTemplate>(
    HR_ONBOARDING_TEMPLATES_PATH,
    'POST',
  );
  const createItem = useApiMutation<OnboardingTemplateItemCreateRequest, OnboardingTemplateItem>(
    onboardingTemplateItemsPath('unset'),
    'POST',
  );
  const createChecklist = useApiMutation<OnboardingChecklistCreateRequest, OnboardingChecklist>(
    HR_ONBOARDING_CHECKLISTS_PATH,
    'POST',
  );
  // No request body — the complete action carries none, exactly like the offer
  // approve/send actions, so the body type is `undefined` and `submit` sends nothing.
  const completeTask = useApiMutation<undefined, OnboardingChecklist>(
    onboardingTaskCompletePath('unset', 'unset'),
    'POST',
  );

  const submitTemplate = (body: OnboardingTemplateCreateRequest): void => {
    createTemplate
      .submit(body)
      .then(() => {
        setTemplatesToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const selectTemplate = (id: string): void => {
    createItem.reset();
    setSelectedTemplateId(id);
  };

  const submitItem = (body: OnboardingTemplateItemCreateRequest): void => {
    if (selectedTemplateId === null) return;
    createItem
      .submit(body, onboardingTemplateItemsPath(selectedTemplateId))
      .then(() => {
        setItemsToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const loadEmployee = (id: string): void => {
    createChecklist.reset();
    completeTask.reset();
    setCompletingTaskId(null);
    setEmployeeId(id);
  };

  const submitChecklist = (body: OnboardingChecklistCreateRequest): void => {
    createChecklist
      .submit(body, HR_ONBOARDING_CHECKLISTS_PATH)
      .then(() => {
        setChecklistsToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const completeOnboardingTask = (checklistId: string, taskId: string): void => {
    setCompletingTaskId(taskId);
    completeTask
      .submit(undefined, onboardingTaskCompletePath(checklistId, taskId))
      .then(() => {
        setCompletingTaskId(null);
        setChecklistsToken((value) => value + 1);
      })
      .catch(() => {
        setCompletingTaskId(null);
      });
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.onboarding')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('hr.onboarding.intro')}</p>
        <p className="wp-small wp-muted" data-testid="w6o-scope-note">
          {t('hr.onboarding.scopeNote')}
        </p>
      </div>

      <OnboardingChecklistsPanel
        employeeId={employeeId}
        onLoadEmployee={loadEmployee}
        listStatus={checklists.status}
        checklists={checklists.data?.data ?? null}
        listError={checklists.error}
        onReload={checklists.reload}
        templates={templates.data?.data ?? null}
        createPending={createChecklist.status === 'pending'}
        createError={createChecklist.error}
        onCreate={submitChecklist}
        completingTaskId={completingTaskId}
        completeError={completeTask.error}
        onComplete={completeOnboardingTask}
        locale={locale}
        timezone={timezone}
      />

      {/* The templates are the configuration behind the checklists; an HR admin opens
          this screen to see where a new joiner has got to, and edits the template
          occasionally. The daily read goes first. */}
      <OnboardingTemplatesPanel
        listStatus={templates.status}
        templates={templates.data?.data ?? null}
        listError={templates.error}
        onReload={templates.reload}
        createPending={createTemplate.status === 'pending'}
        createError={createTemplate.error}
        created={createTemplate.status === 'ok' ? createTemplate.data : null}
        onCreate={submitTemplate}
        selectedTemplateId={selectedTemplateId}
        onSelect={selectTemplate}
        itemsStatus={items.status}
        items={items.data?.data ?? null}
        itemsError={items.error}
        onItemsReload={items.reload}
        itemCreatePending={createItem.status === 'pending'}
        itemCreateError={createItem.error}
        itemCreated={createItem.status === 'ok' ? createItem.data : null}
        onCreateItem={submitItem}
      />
    </div>
  );
}
